import classNames from 'classnames';
import { ComponentProps, useEffect, useRef, useState } from 'react';
import {
  IoChevronBack as ArrowLeftIcon,
  IoChevronForward as ArrowRightIcon,
} from 'react-icons/io5';

type ChipButtonProps = ComponentProps<'button'> & {
  active?: boolean;
};

const ChipButton = ({ children, active, ...props }: ChipButtonProps) => {
  return (
    <button
      className={classNames(
        'px-2 py-1 rounded-md text-sm font-medium',
        active ? 'bg-secondary-default text-secondary-contrast' : 'bg-gray-100 text-gray-800'
      )}
      type="button"
      {...props}
    >
      {children}
    </button>
  );
};

type Props = {
  /**
   * children can only be of type ChipButton
   */
  children?: React.ReactElement<ChipButtonProps>[] | React.ReactElement<ChipButtonProps>;
};

/**
 *
 * Should be false when reached the end of the container,
 * or when the container is not scrollable.
 * Should work with both horizontal and vertical scroll,
 * and react to scroll events.
 * It should return a boolean for each direction.
 * A boolean is true when the container is scrollable in that direction,
 * with a padding of 15px
 */
function useIsVerticallyScrollable(element?: HTMLElement | null) {
  const [scrollable, setScrollable] = useState({
    right: false,
    left: false,
  });

  useEffect(() => {
    if (!element) {
      return;
    }

    const handleScroll = () => {
      setScrollable({
        right: element.scrollLeft + element.clientWidth < element.scrollWidth,
        left: element.scrollLeft > 0,
      });
    };

    handleScroll();

    element.addEventListener('scroll', handleScroll);

    return () => {
      element.removeEventListener('scroll', handleScroll);
    };
  }, [element]);

  return scrollable;
}

type ScrollButtonProps = ComponentProps<'button'> & {
  direction: 'left' | 'right';
};

const ScrollButton = ({ direction, ...btnProps }: ScrollButtonProps) => {
  return (
    <div
      className={classNames('absolute h-full w-8 from-white', {
        'right-0 top-0 bg-gradient-to-l': direction === 'right',
        'left-0 top-0 bg-gradient-to-r': direction === 'left',
      })}
    >
      <button
        {...btnProps}
        className={classNames('flex items-center h-full w-full', {
          'justify-end': direction === 'right',
          'justify-start': direction === 'left',
        })}
        type="button"
      >
        {direction === 'right' ? <ArrowRightIcon /> : <ArrowLeftIcon />}
      </button>
    </div>
  );
};

const ChipsContainer = ({ children }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { right, left } = useIsVerticallyScrollable(containerRef.current);

  const SCROLL_FACTOR = 100;

  return (
    <div className="relative w-full">
      <div
        ref={containerRef}
        className="flex items-center gap-2 mb-4 w-full overflow-x-auto no-scrollbar grad"
      >
        {children}
      </div>

      {left && (
        <ScrollButton
          direction="left"
          onClick={() => {
            containerRef.current?.scrollBy({
              left: -SCROLL_FACTOR,
              behavior: 'smooth',
            });
          }}
        />
      )}

      {right && (
        <ScrollButton
          direction="right"
          onClick={() => {
            containerRef.current?.scrollBy({
              left: SCROLL_FACTOR,
              behavior: 'smooth',
            });
          }}
        />
      )}
    </div>
  );
};

export const Chips = {
  Container: ChipsContainer,
  Button: ChipButton,
};
